import {useMutation, UseMutationResult, useQueryClient} from '@tanstack/react-query';
import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import InputWithCharacterCounter from '../../../../components/slides/highlighted/common/InputWithCharacterCounter';
import {usePresentationThemeDetailQuery} from '../../../../hooks/usePresentationThemeDetailQuery';
import {type TypedTranslation} from '../../../../i18n';
import Label from '../../../../incase-domain/components/label/Label';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import VerticalNavigation from '../../../../incase-domain/components/verticalNavigation/VerticalNavigation';
import {FONT_SIZE, IPresentationTheme} from '../../../../incase-domain/presentationTheme';
import {migrateLegacyThemeStyles} from '../../../../utils/presentationTheme';
import {createPresentationTheme, updatePresentationThemeService} from '../../../../service/presentationTheme.service';
import {IUpdateError} from '../../../../store/actions/types/error.types';
import {defaultFonts} from '../../../../utils/defaultFonts';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import LoadingSpinnerComponent from '../../../components/LoadingSpinnerComponent';
import PresentationThemeFavoriteColors from './PresentationThemeFavoriteColors';
import PresentationThemeFonts from './PresentationThemeFonts';
import PresentationThemeFontSizes from './PresentationThemeFontSizes';
import PresentationThemeTextElement from './PresentationThemeTextElement';
import PresentationThemeQuestionElement from './questionElement/PresentationThemeQuestionElement';
import PresentationThemeStaticElement from './staticElement/PresentationThemeStaticElement';
import PresentationThemeFormElement from './formElement/PresentationThemeFormElement';
import PresentationThemeShapeElement from './shapeElement/PresentationThemeShapeElement';
import ThemePreviewPanel from './ThemePreviewPanel';
import {getThemeNavEntry, PresentationThemeNavItem, THEME_NAV} from './themeNav';

export type PresentationThemeModalMode = 'create' | 'edit';

const PRESENTATION_THEME_FORM_ID = 'presentation_theme_form';

interface PresentationThemeModalProps {
    isOpen: boolean;
    onClose: () => void;
    mode: PresentationThemeModalMode;
    themeId?: string;
    readOnly?: boolean;
}

function getEmptyTheme(fontSizes: IPresentationTheme['fontSizes']): IPresentationTheme {
    return {
        themeName: '',
        favoriteColors: [],
        fontSizes,
        elementStyles: {
            textElement: {
                color: undefined,
                borderRadius: undefined,
                backgroundColor: undefined,
                fontSize: undefined,
            },
            questionElement: {
                questionFontSize: undefined,
                answerFontSize: undefined,
                commentFontSize: undefined,
            },
        },
        fonts: [],
        defaultFonts,
        lastModified: 0,
    };
}

function PresentationThemeModal({isOpen, onClose, mode, themeId, readOnly = false}: PresentationThemeModalProps): JSX.Element {
    const {t} = useTranslation();
    const client = useQueryClient();

    const createThemeMutation: UseMutationResult<
        IPresentationTheme,
        {
            message: TypedTranslation;
        },
        IPresentationTheme
    > = useMutation((theme) => createPresentationTheme(theme), {
        onSuccess: () =>
            client.invalidateQueries({queryKey: QUERY_KEYS.theme.lists()}).then(() => {
                toast.success(t('presentationThemesPage.toasts.presThemeCreateSuccess'));
                onClose();
            }),
        onError: (response) => {
            if (response.message.includes('tooManyThemes')) {
                toast.error(t(response.message));
            }
        },
    });

    const updateThemeMutation: UseMutationResult<boolean, IUpdateError, {themeId: string; theme: IPresentationTheme}> = useMutation(
        (variables) => updatePresentationThemeService(variables.themeId, variables.theme),
        {
            onSuccess: () => {
                Promise.all([
                    client.invalidateQueries({queryKey: QUERY_KEYS.theme.detail(themeId || '')}),
                    client.invalidateQueries({queryKey: QUERY_KEYS.theme.lists()}),
                ]).then(() => {
                    toast.success(t('presentationThemesPage.toasts.presThemeEditSuccess'));
                    onClose();
                });
            },
        }
    );

    const isSaving = createThemeMutation.isLoading || updateThemeMutation.isLoading;

    const handleSave = (theme: IPresentationTheme): void => {
        if (readOnly || isSaving) return;

        if (mode === 'create') {
            createThemeMutation.mutate(theme);
            return;
        }

        updateThemeMutation.mutate({
            themeId: themeId || '',
            theme,
        });
    };

    const modalHeader = (() => {
        if (readOnly) return t('presentationThemesPage.viewTheme');
        if (mode === 'create') return t('presentationThemesPage.createTheme');
        return t('presentationThemesPage.editTheme');
    })();

    return (
        <Modal
            isOpen={isOpen}
            header={modalHeader}
            description={t('presentationThemesPage.presentationThemes')}
            icon="solar:pallete-2-bold"
            size="xl"
            noPadding
            closeOnClickOutside={false}
            onCancel={onClose}
            showConfirm={!readOnly}
            confirmText={t('common.save')}
            formId={PRESENTATION_THEME_FORM_ID}
            confirmDisabled={isSaving}
            showCancel={readOnly}
            cancelText={t('common.close')}
        >
            <PresentationThemeForm mode={mode} themeId={themeId} readOnly={readOnly} onSubmit={handleSave} />
        </Modal>
    );
}

interface PresentationThemeFormProps {
    mode: PresentationThemeModalMode;
    themeId?: string;
    readOnly: boolean;
    onSubmit: (theme: IPresentationTheme) => void;
}

function ThemeNavSection({title, className, children}: {title: string; className?: string; children: React.ReactNode}): JSX.Element {
    return (
        <div className={className}>
            <div className="c-verticalNavigation__header">
                <div className="c-verticalNavigation__header_title">{title}</div>
            </div>
            {children}
        </div>
    );
}

function PresentationThemeForm({mode, themeId, readOnly, onSubmit}: PresentationThemeFormProps): JSX.Element {
    const {t} = useTranslation();

    const initialFontSizes = useMemo(
        () => [
            {size: FONT_SIZE.EXTRA_SMALL, value: 0.5},
            {size: FONT_SIZE.SMALL, value: 1},
            {size: FONT_SIZE.MEDIUM, value: 1.5},
            {size: FONT_SIZE.LARGE, value: 2},
            {size: FONT_SIZE.EXTRA_LARGE, value: 2.5},
        ],
        []
    );

    const [activeItem, setActiveItem] = useState<PresentationThemeNavItem>(PresentationThemeNavItem.GENERAL);
    const activeEntry = getThemeNavEntry(activeItem);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const [presentationTheme, setPresentationTheme] = useState<IPresentationTheme>(() => getEmptyTheme(initialFontSizes));

    const fetchTheme = usePresentationThemeDetailQuery(mode === 'edit' ? themeId : undefined, {
        enabled: mode === 'edit' && !!themeId,
    });

    useEffect(() => {
        if (mode !== 'edit' || !fetchTheme.data) return;

        setPresentationTheme(
            migrateLegacyThemeStyles({
                _id: fetchTheme.data._id,
                themeName: fetchTheme.data.themeName,
                favoriteColors: fetchTheme.data.favoriteColors,
                fontSizes: fetchTheme.data.fontSizes || initialFontSizes,
                elementStyles: fetchTheme.data.elementStyles,
                fonts: fetchTheme.data.fonts,
                defaultFonts: fetchTheme.data.defaultFonts ? fetchTheme.data.defaultFonts : defaultFonts,
                lastModified: fetchTheme.data.lastModified,
            })
        );
    }, [mode, fetchTheme.data, initialFontSizes]);

    const hasGeneralError = !presentationTheme.themeName || presentationTheme.themeName.trim().length === 0;
    const showGeneralError = saveAttempted && hasGeneralError;
    const showPreview = activeItem !== PresentationThemeNavItem.GENERAL;

    const handleSubmit = (event: React.FormEvent): void => {
        event.preventDefault();
        if (readOnly) return;

        setSaveAttempted(true);

        if (hasGeneralError) {
            setActiveItem(PresentationThemeNavItem.GENERAL);
            return;
        }

        onSubmit(presentationTheme);
    };

    const updatePresentationTheme: React.Dispatch<React.SetStateAction<IPresentationTheme>> = readOnly
        ? () => undefined
        : setPresentationTheme;

    const navItems = THEME_NAV.map((item) => ({
        label: t(item.labelKey),
        icon: item.icon,
        error: item.id === PresentationThemeNavItem.GENERAL && showGeneralError,
    }));

    const isLoadingEdit = mode === 'edit' && fetchTheme.isLoading;

    return (
        <form id={PRESENTATION_THEME_FORM_ID} className="c-editRoomModal c-presentationThemeModal" onSubmit={handleSubmit} noValidate>
            <div className="c-editRoomModal__navigation">
                <VerticalNavigation
                    items={navItems}
                    onChange={(index) => setActiveItem(THEME_NAV[index].id)}
                    initialIndex={THEME_NAV.findIndex((item) => item.id === activeItem)}
                />
            </div>

            <div className="c-editRoomModal__content">
                {isLoadingEdit ? (
                    <LoadingSpinnerComponent />
                ) : (
                    <>
                        <div
                            className={
                                showPreview
                                    ? 'c-editRoomModal__content_body c-presentationThemeModal__body'
                                    : 'c-editRoomModal__content_body'
                            }
                        >
                            <div
                                className={classNames({
                                    'c-presentationThemeModal__controls': showPreview,
                                    'c-presentationThemeModal__controls--readOnly': readOnly,
                                })}
                            >
                                {activeEntry.group === 'general' && (
                                    <ThemeNavSection title={t(activeEntry.labelKey)} className="u-flex u-flex-col u-gap-2">
                                        <div>
                                            <Label>{t('presentationThemesPage.themeName')}</Label>
                                            <InputWithCharacterCounter
                                                className="c-sidepanel__input"
                                                placeholder={t('presentationThemesPage.themeName')}
                                                maxLength={15}
                                                value={presentationTheme.themeName}
                                                disabled={readOnly}
                                                onChange={(value) => {
                                                    setPresentationTheme({...presentationTheme, themeName: value});
                                                }}
                                            />
                                            {showGeneralError && (
                                                <div className="is-error">{t('presentationThemesPage.validation.themeNameRequired')}</div>
                                            )}
                                        </div>
                                        <PresentationThemeFavoriteColors
                                            favoriteColorsList={presentationTheme.favoriteColors}
                                            setPresentationTheme={updatePresentationTheme}
                                            presentationTheme={presentationTheme}
                                        />
                                        <PresentationThemeFontSizes
                                            setPresentationTheme={updatePresentationTheme}
                                            presentationTheme={presentationTheme}
                                        />
                                        <PresentationThemeFonts
                                            setPresentationTheme={updatePresentationTheme}
                                            presentationTheme={presentationTheme}
                                        />
                                    </ThemeNavSection>
                                )}

                                {activeEntry.group === 'text' && (
                                    <ThemeNavSection title={t(activeEntry.labelKey)}>
                                        <PresentationThemeTextElement
                                            presentationTheme={presentationTheme}
                                            setPresentationTheme={updatePresentationTheme}
                                        />
                                    </ThemeNavSection>
                                )}

                                {activeEntry.group === 'question' && activeEntry.element && (
                                    <ThemeNavSection title={t(activeEntry.labelKey)}>
                                        <PresentationThemeQuestionElement
                                            presentationTheme={presentationTheme}
                                            setPresentationTheme={updatePresentationTheme}
                                            questionType={activeEntry.element}
                                            shared={activeEntry.shared}
                                        />
                                    </ThemeNavSection>
                                )}
                                {activeEntry.group === 'content' && activeEntry.element && (
                                    <ThemeNavSection title={t(activeEntry.labelKey)}>
                                        <PresentationThemeStaticElement
                                            presentationTheme={presentationTheme}
                                            setPresentationTheme={updatePresentationTheme}
                                            contentType={activeEntry.element}
                                        />
                                    </ThemeNavSection>
                                )}

                                {activeEntry.group === 'form' && activeEntry.element && (
                                    <ThemeNavSection title={t(activeEntry.labelKey)}>
                                        <PresentationThemeFormElement
                                            presentationTheme={presentationTheme}
                                            setPresentationTheme={updatePresentationTheme}
                                            formType={activeEntry.element}
                                        />
                                    </ThemeNavSection>
                                )}

                                {activeEntry.group === 'shape' && activeEntry.element && (
                                    <ThemeNavSection title={t(activeEntry.labelKey)}>
                                        <PresentationThemeShapeElement
                                            presentationTheme={presentationTheme}
                                            setPresentationTheme={updatePresentationTheme}
                                            shapeType={activeEntry.element}
                                            shared={activeEntry.shared}
                                        />
                                    </ThemeNavSection>
                                )}
                            </div>

                            {showPreview && (
                                <div className="c-presentationThemeModal__preview">
                                    <ThemePreviewPanel activeItem={activeItem} presentationTheme={presentationTheme} />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </form>
    );
}

export default PresentationThemeModal;
